import type { StyleRule } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import {
    DUO_COLUMN_WIDTH,
    ROW_GAP_MOBILE,
    COLUMN_WIDTH_100,
} from '@modules/page-builder/config/column-constants';

export const DuoImageModuleContainer: StyleRule = ({
    theme,
    isDuoPortrait,
}) => ({
    display: 'flex',
    flexFlow: 'column',

    [theme.screen.laptop]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        width: '100%',
        columnGap: pxToRem(40),
        gridTemplateRows: `1fr`,
        maxWidth: isDuoPortrait
            ? pxToRem(COLUMN_WIDTH_100)
            : pxToRem(DUO_COLUMN_WIDTH + 100),
        margin: '0 auto',
    },
});

export const DuoImageContainerImage: (
    isMedium: boolean,
    isSmall?: boolean,
    isPortrait?: boolean,
    height?: number,
    width?: number,
    mobileHeight?: number,
    mobileWidth?: number
) => StyleRule<{
    columns: number;
    mobile: boolean;
    isDuoPortrait?: boolean;
}> =
    (isMedium, isSmall, isPortrait, height, width) =>
    ({ columns, mobile, theme, isDuoPortrait }) => {
        if (isSmall) {
            // Retain original small image aspect ratio but bump width to 500
            return {
                margin: 'auto',
                width: pxToRem(500),
                height: 'auto',
                '& > div': {
                    maxHeight: 'none !important',
                    '& > #detail-single-image-shell': {
                        aspectRatio: `${width || 1} / ${height || 1}`,
                        height: 'auto',
                    },
                },
            };
        }

        if (!isPortrait) {
            // Retain original landscape aspect ratio but bump width to 750
            return {
                margin: '0',
                width: pxToRem(750),
                height: 'auto',
                '& > div': {
                    maxHeight: 'none !important',
                    '& > #detail-single-image-shell': {
                        aspectRatio: `${width || 1} / ${height || 1}`,
                        height: 'auto',
                    },
                },
            };
        }

        // Now only apply this style to portrait / medium non mobile images.
        if (isPortrait || (isMedium && !mobile)) {
            return {
                margin: '0',
                width: isDuoPortrait ? '100%' : pxToRem(750),
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                // Override height constraints only for the image container
                '& > div': {
                    maxHeight: 'none !important',
                    '& > #detail-single-image-shell': {
                        aspectRatio: `${width || 1} / ${height || 1}`,
                        height: 'auto',
                    },
                },
            };
        }

        if (mobile) {
            return Object.assign({
                margin: '0 auto',
                '&:last-child':
                    columns > 1
                        ? {
                              margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
                          }
                        : undefined,
            });
        }
        return {
            margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
            [theme.screen.laptop]: {
                margin: 'auto',
                '&:has(.isMedium)': {
                    margin: '0 auto',
                },
            },
        };
    };

export const DuoImageModuleContainerPageBuilder: StyleRule = ({ theme }) => ({
    display: 'flex',
    flexFlow: 'column',

    [theme.screen.laptop]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: pxToRem(204),
        gridTemplateRows: `1fr`,
        maxWidth: pxToRem(DUO_COLUMN_WIDTH),
        margin: 'auto',
    },
});

export const DuoImageContainerImagePageBuilder: (
    isMedium: boolean
) => StyleRule<{
    columns: number;
    mobile: boolean;
}> =
    (isMedium) =>
    ({ columns, mobile, theme }) => {
        if (isMedium && !mobile) {
            return { margin: '0 auto' };
        }

        if (mobile) {
            return Object.assign({
                margin: '0 auto',
                '&:last-child':
                    columns > 1
                        ? {
                              margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
                          }
                        : undefined,
            });
        }
        return {
            margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
            [theme.screen.laptop]: {
                height: 'auto !important',
                margin: 'auto',
                display: 'flex',
                alignItems: 'start',
                justifyContent: 'center',
                minHeight: '100%',
                '&:has(.isMedium)': {
                    margin: '0 auto',
                },
            },
        };
    };
